import { clone, each, get, isEqual, remove } from 'lodash';
import * as React from 'react';
import { findWhere } from '../../../util';
import { HypothesisFindingSupport } from './hypothesis-supports';
import { INSIGHT_PRIORITY_OPTIONS } from "../../../constants";
import * as userUtil from '../../../stores/user/util';
import { userHasRole } from './../../../../app/util';
import { BmcSelector } from './../bmc-selector/bmc-selector';

import { components } from 'react-select';
import AsyncCreatableSelect from 'react-select/async-creatable';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, Button, Dialog, DialogActions, DialogTitle, Divider, IconButton, Popper, Stack, TextField, Tooltip } from '@mui/material';
import MaterialButton from '@mui/material/Button';
import { Clear, LocalOffer } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import { Theme } from '@mui/material/styles';
import { toast } from 'sonner';
import { appLog } from '../../../../app/util';
import { checkRole, getEntryById } from '../../../stores/jotai/base';
import IwButton from '../../app/IwButton';
import IwSwitch from '../../app/IwSwitch';
import CategoryManagerModal from './category-manager-modal';
import { renderErrorToast } from '../../../../lib/ErrorToastMessage';


import { aiIndicationIcons } from '../../../../lib/teacup/lib/ai-indicator';
import DeleteOutlinedIcon from '../../../../icons/DeleteOutlinedIcon';

const INSIGHT_RATING_TOOLTIP_TEXT = "Insight Priority";

export const formatCategoriesForFavorites = (categories) => {
  if (categories === undefined) return [];

  let formattedData = [];
  for (let c of categories) {
    if (c.options.favorited) {
      formattedData.push(c);
    }
  }

  return formattedData;
};

export function createTagBadge(
  theme,
  selectedTag,
  updateSelections,
  clearable?
) {
  return (
    selectedTag.label !== undefined && (
      <div
        className="tag"
        style={{
          // backgroundColor: selectedTag.color ? selectedTag.color : '#FFFFFF',
          backgroundColor: selectedTag.color
            ? selectedTag.color
            : theme.custom.disabledColor,
          padding: "2px 6px",
          borderRadius: "4px",
          border: `1px solid ${theme.custom.borderGrey}`,
          display: "flex",
          gap: "5px",
          alignItems: "center",
          fontSize: "15px",
          height: "28px",
          color: theme.custom.mainHeaderColor
        }}
      >
        <div
          style={{
            maxWidth: "150px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
        >
          {selectedTag.label}
        </div>
        {clearable ? (
          <div
            className="remove-tag-icon"
            onClick={() => {
              updateSelections(selectedTag);
            }}
          >
            <CancelIcon sx={{ fontSize: "16px" }} />
          </div>
        ) : null}
      </div>
    )
  );
}

// conditional to report whether we should reset our state to default
const shouldReinitialize = (nextProps: Props, oldProps: Props): boolean => {
  // if we've switched interviews
  if (nextProps.interviewID !== oldProps.interviewID) return true;

  // user has de-selected a finding (focused on artifact editor)
  if (oldProps.finding && !nextProps.finding) return true;

  // user has changed their selection
  if (oldProps?.quote !== nextProps?.quote) return true;

  return false;
};

// turn the api version of hypothesis links to the limited version our component here needs
const hypothesisLinksToHypothesisSupporters = (
  hypothesisLinks: HypothesisSupports
): HypothesisSupporter[] => {
  return hypothesisLinks.map((link) => {
    return {
      hypothesisKey: link.key,
      supports: link.supports,
      findingID: link.findingID
    };
  });
};

interface Refs {
  [key: string]: React.Component<any, any>;
  comment: Input;
}

interface Props {
  activeArtifactName: string;
  defaultTypes: any;
  user: User;
  team: TeamState;
  cohort: CohortState;
  base: any;
  canvas?: any;
  baseCreateCategory: any;
  baseUpdateCategory: any;
  baseToggleAllFavoritedTags: any;
  baseDeleteCategory: any;
  customFindingTypes: any[];
  findingLinksData: any[];

  finding: Finding;
  findingTypes: FindingTypes;

  interviewState: any;

  appState: AppState;

  quote: string; // the text selected by the artifact editor

  canvasCategories: { [key: string]: CanvasCategory };
  canvasEntries: { [key: string]: CanvasEntry };

  interviewID?: string;

  create(
    comment: string,
    findingTypeID: string,
    hypothesisSupporters: HypothesisSupporter[],
    rating: number
  ): void;
  update(
    comment: string,
    findingTypeID: string,
    hypothesisSupporters: HypothesisSupporter[],
    rating: number,
    isChanged:boolean,
  ): void;
  isSaving: boolean;
  createSaving: boolean;

  renderMaterialArtifact?: any;
  clearSelectedFinding: any;
  canEditCanvas: boolean;
  isDraft: Boolean;
  theme: Theme;
  onDelete:Function;
}

const INITIAL_STATUS = {
  changed: {
    comment: false
  },
  hasQuote: false
};
interface EditStatus {
  hasQuote: boolean;
  changed: {
    comment: boolean;
  };
}

interface State {
  typesID?: string[];
  comment: string;
  editStatus: EditStatus;
  initialized: boolean;
  // Maintain a mutable list of all the hypothesis supporters
  hypothesisSupporters: {
    key: string;
    category: string;
    hypothesisKey: string;
    supports: boolean;
  }[];

  isNewHypothesisValid: boolean;

  hasClickedOnPlaceholder: boolean;

  // Is the latest placeholder to be added by the BMC selector
  // a "sub" category
  isLatestNewHypothesisASub: boolean;

  inputValue: string;
  hoveredItem: any;
  selectedTags: Array<object>;
  selectedRating: number;
  creatingNew: boolean;
  customCatContainerWidth: any;
  //categoryData: Array<object>;
  managerModalOpen: boolean;
  optionAnchor: any;
  confirmationDialogOpen: boolean;
  lastSeenCategorySet: any;
  selectedOptions: Array<object>;
  findingLinksData: Array<object>;
}

const SECTION_SUB_TITLE_STYLE = {
  color: "rgba(0, 0, 0, 0.6)",
  display: "inline-block",
  float: "left",
  marginLeft: "10px",
  backgroundColor: "white"
};

const startingCategoryData = [
  {
    value: "iw",
    label: "IW - Default Tags",
    favorite: true,
    tags: [
      { value: "1", label: "General Insight", color: "#fff9b3" },
      { value: "feature", label: "New Feature", color: "#c6f8b4" },
      { value: "question", label: "New Question", color: "#cbe3ff" },
      { value: "interview", label: "Interview Candidate", color: "#eed2ff" },
      { value: "hypothesis", label: "New Hypothesis", color: "#fdb9cb" },
      { value: "feedback", label: "Feedback", color: "#ffcf9c" }
    ]
  },
  {
    value: "org",
    label: "Org Tags",
    favorite: false,
    tags: [
      { value: "org1", label: "Org1" },
      { value: "org2", label: "Org2" }
    ]
  },
  {
    value: "cohort",
    label: "Cohort Tags",
    favorite: false,
    tags: [
      {
        value: "coho1",
        label: "Coho1 With A Crazy Long Name That Doesnt Fit On The Page"
      },
      { value: "coho2", label: "Coho2" },
      { value: "coho3", label: "Coho3" },
      { value: "coho4", label: "Coho4" },
      { value: "coho5", label: "Coho5" },
      { value: "coho6", label: "Coho6" }
    ]
  },
  {
    value: "team",
    label: "Team Tags and Really Long Stuff And All That",
    favorite: false,
    tags: [
      { value: "team1", label: "Team1" },
      { value: "team2", label: "Team2" },
      { value: "team3", label: "Team3" },
      { value: "team4", label: "Team4" }
    ]
  },
  {
    value: "custom",
    label: "Custom Tags",
    favorite: false,
    tags: [
      // {value: 'cust1', label: 'Cust1'},
      // {value: 'cust2', label: 'Cust2'},
      // {value: 'cust3', label: 'Cust3'},
      // {value: 'cust4', label: 'Cust4'},
      // {value: 'cust5', label: 'Cust5'}
    ]
  }
];

const formatData = () => {
  return startingCategoryData.map((g) => {
    return { label: g.label, options: g.tags };
  });
};

export class FindingEditor extends React.Component<Props, State> {
  refs: Refs;

  state: State = {
    editStatus: INITIAL_STATUS,
    typesID: [],
    comment: "",
    hypothesisSupporters: [],
    isNewHypothesisValid: true,
    selectedRating: 0,
    hasClickedOnPlaceholder: false,
    isLatestNewHypothesisASub: false,
    // State values for new multi-category selector
    inputValue: "",
    hoveredItem: null,
    selectedTags: [],
    creatingNew: false,
    customCatContainerWidth: null,
    // categoryData: [],
    managerModalOpen: false,
    initialized: false,
    optionAnchor: null,
    confirmationDialogOpen: false,
    lastSeenCategorySet: undefined,
    selectedOptions: []
  };

  canEditFinding = () => {
    const { finding, canEditCanvas, user } = this.props;
    let canEdit = true;

    if (!!finding) {
      if (!canEditCanvas && finding?.userID !== user.userID) {
        canEdit = false;
      }
    }

    return canEdit;
  };

  shouldShowActionItem = (tag) => {
    if (tag.view !== "global") {
      return true;
    } else {
      return checkRole(this.props.base, "isSystemAdministrator");
    }
  };

  // Build our support list off what's in the state for our finding
  buildHypoSupporters = (props) => {
    if (props.finding === undefined) return [];

    let hypothesisLinks = get(
      props.finding,
      "hypothesisLinks"
    ) as HypothesisSupports[];

    let base = [];

    for (let i = 0; i < props?.findingLinksData?.length; i++) {
      const __link = props.findingLinksData[i];

      if (__link.findingID === props.finding.findingID) {
        let foundCategoryID = undefined;

        try {
          let matchingEntry = getEntryById(this.props.canvas, __link.entryID);
          //@ts-ignore
          foundCategoryID = matchingEntry.categoryID;
        } catch (e) {}

        base.push({
          hypothesisKey: __link.entryID,
          supports: __link.supports,
          category: foundCategoryID,
          key: __link.linkID
        });
      }
    }

    // each(hypothesisLinks, hypo => {
    //     let foundCategoryID = undefined;

    //     try {
    //         let matchingEntry = getEntryById(props.base, hypo.entryID);
    //         //@ts-ignore
    //         foundCategoryID = matchingEntry.categoryID;
    //     }
    //     catch (e) {

    //     }

    //     base.push({
    //         hypothesisKey: hypo.entryID,
    //         supports: hypo.supports,
    //         category: foundCategoryID,
    //         key: uuid.generate()
    //     });
    // });

    //console.log(base);

    return base;
  };

  componentWillReceiveProps(nextProps: Props) {
    // if (this.state.initialized === false) {
    //     this.setState({
    //         selectedTags: this.props.defaultTypes,
    //         initialized: true
    //     })
    // }

    // if we've switched findings, make sure we have the correct state
    if (nextProps.finding) {
      if (this.refs.comment)
        this.refs.comment._setValue(nextProps.finding.comment);

      this.setState({
        comment: nextProps.finding.comment
      });
    }

    if (nextProps.finding) {
      if (this.props.finding) {
        if (this.props.finding.id !== nextProps.finding.id) {
          if (this.refs.comment) {
            this.refs.comment._setValue(nextProps.finding.comment);
          }

          if (nextProps.finding?.rating?.global?.rating > 0) {
            const matchingOptions = INSIGHT_PRIORITY_OPTIONS.filter(
              (option) => {
                return (
                  option.value === nextProps.finding?.rating?.global?.rating ||
                  (nextProps.finding?.rating?.global?.rating === 1 &&
                    option.value === 2)
                );
              }
            );

            this.setState({
              selectedOptions: matchingOptions
            });
          } else {
            this.setState({
              selectedOptions: []
            });
          }

          this.setState({
            comment: nextProps.finding.comment,
            selectedTags: nextProps.defaultTypes,
            //@ts-ignore
            selectedRating: nextProps.finding?.rating?.global?.rating || 0
          });
        }
      }
    }

    if (nextProps.interviewID !== this.props.interviewID) {
      if (this.refs.comment) {
        this.refs.comment._setValue("");
      }
      this.setState({
        comment: "",
        selectedTags: nextProps.defaultTypes,
        selectedRating: 0,
        selectedOptions: []
      });
    }

    if (shouldReinitialize(nextProps, this.props)) {
      this.initialize(nextProps);
    }
  }

  initialize(props) {
    // console.log(`initialization stamp ${JSON.stringify(props.finding)}`)
    if (props.finding) {
      this.setState({
        comment: props.finding.comment,
        hypothesisSupporters: this.buildHypoSupporters(props),
        initialized: true,
        editStatus: INITIAL_STATUS
      });
    } else {
      this.setState({
        comment: "",
        hypothesisSupporters: this.buildHypoSupporters(props),
        selectedTags: props.defaultTypes,
        initialized: true,
        editStatus: INITIAL_STATUS
      });

      if (this.refs.comment) {
        this.refs.comment._setValue("");
      }
    }
  }

  getCurrentTypesString = () => {
    let buffer = [];

    const selectedTags = this.state;

    //@ts-ignore
    for (let i = 0; i < selectedTags.selectedTags.length; i++) {
      //@ts-ignore
      buffer.push(selectedTags.selectedTags[i].customFindingTypeID);
    }

    return buffer.join(",");
  };

  save = (hypothesisSupporters,isChanged) => {
    this.setState({
      lastSeenCategorySet: undefined
    });

    const comment = this.state.comment;
    const rating = this.state.selectedRating;

    // We need to compare what's missing from the original set and potentially create some removal requests
    const originalList = this.buildHypoSupporters(this.props);

    // Go through each of the original links or "whats currently in the DB"
    each(originalList, (original) => {
      let found = false;

      // For each one see if a match still exists
      each(hypothesisSupporters, (_new) => {
        if (_new.hypothesisKey === original.hypothesisKey) {
          found = true;
        }
      });

      // If it doesn't drop it
      if (found === false) {
        this.handleRemovingHypothesisSupporterData(original.hypothesisKey);
      }
    });

    if (!!this.props.finding) {
      // we're modifying an existing finding
      this.props.update(
        comment,
        this.getCurrentTypesString(),
        hypothesisSupporters,
        rating,
        isChanged
      );
    } else {
      // we're creating a new finding
      this.props.create(
        comment,
        this.getCurrentTypesString(),
        hypothesisSupporters,
        rating
      );
    }
  };

  getComment = () => {
    return this.refs.comment.getValue();
  };

  setFindingType(type: FindingType) {
    this.setState({ typeID: type.id });
  }

  // render the finding type buttons which select finding type
  renderFavorites = () => {
    // TODO: Make finding type the default
    const canEdit = this.canEditFinding();
    const { theme } = this.props;
    const findingSelectors = formatCategoriesForFavorites(
      this.props.customFindingTypes
    ).map((fav) => {
      let isClicked = this.state.selectedTags.some(
        (t) => t?.customFindingTypeID === fav.customFindingTypeID
      );
      let findingColor = fav.color || theme?.custom.disabledColor;

      return (
        <div>
          <MaterialButton
            key={fav.customFindingTypeID}
            disabled={!canEdit || this.props.quote?.textCache === undefined}
            onClick={(e) => this.updateSelections(fav, e)}
            startIcon={
              isClicked ? (
                <CheckCircleOutlineOutlinedIcon
                  sx={{ color: theme.custom.mainButtonHighlight }}
                />
              ) : null
            }
            sx={{
              textTransform: "none",
              boxShadow: isClicked
                ? `0 0 0 2px ${theme?.custom.mainButtonHighlight} !important`
                : `0 0 0 1px ${theme?.custom.borderGrey} !important`,
              height: "35px",
              backgroundColor: `${findingColor} !important`,
              color: theme?.custom.mainHeaderColor,
              maxWidth: "200px",
              padding: "0px 6px",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
              ":hover": {
                backgroundColor: `${findingColor} !important`,
                boxShadow: `0 0 0 2px ${theme?.custom.mainButtonHighlight} !important`
              }
            }}
          >
            {fav.label}
          </MaterialButton>
        </div>
      );
    });
    if (
      findingSelectors.length > 0 &&
      this.props.base.favoritedTagsVisibility
    ) {
      return (
        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            paddingBottom: "15px",
            justifyContent: "center"
          }}
        >
          {findingSelectors}
        </div>
      );
    }
  };

  isSelectionEmpty = () => {
    const { finding, quote, user, cohort } = this.props;

    // If a finding is selected then this can't be false
    if (finding !== undefined) return false;

    // Ignore this check if the user is an instructor
    const isInstructor = userHasRole("Instructor", user, cohort);

    if (isInstructor) return false;

    if (quote) return false;

    return !finding || !finding?.quote;
  };

  getColorFromCustomCategory = (categoryID: string) => {
    let buffer = "white";

    for (let i = 0; i < this.props.customFindingTypes.length; i++) {
      if (this.props.customFindingTypes[i].customFindingTypeID === categoryID) {
        const possibleColor = this.props.customFindingTypes[i].color;

        if (typeof possibleColor === "string") {
          buffer = possibleColor;
        }
      }
    }

    return buffer;
  };

  renderArtifact() {
    const { quote, theme } = this.props;
    //const findingType = this.state.typesID![0] || this.getDefaultFindingType();

    let summaryStyle = {
      backgroundColor: "white",
     // fontStyle: quote?.textCache ? "italic" : "normal",
      textOverflow: "ellipsis",
      overflow: "hidden",
      fontSize:"15px",
      display: "-webkit-box",
      "-webkit-line-clamp": "2",
      "-webkit-box-orient": "vertical",
      // color: quote?.textCache
      //   ? theme?.custom.mainButtonHighlight
      //   : theme?.custom.mainHeaderColor
    };

    // if it already has a textCache just render that, it should be pretty close to up-to-date
    return (
      <Box sx={{ padding: "1px 12px 12px 0" }}>
        <Box sx={summaryStyle} component="span">
          {
            (quote?.source === "note" || quote?.source === "transcript")
              ? (quote?.selection && quote?.textCache && `"${quote.textCache}"`)
              : (quote?.textCache && `"${quote.textCache}"`)
            || "Select text to create an Insight"
          }
        </Box>
      </Box>
    );
  }

  getDefaultFindingType() {
    const { findingTypes, user, team, cohort } = this.props;

    const general = findWhere(findingTypes, { name: "General Insight" });
    const feedback = findWhere(findingTypes, { name: "Feedback" });
    const userRoles = userUtil.getRoles(user, team, cohort);
    const isOnlyInstructor = userUtil.isInstructor(userRoles);

    // if user is only an instructor, they can only use the
    // Feedback finding type, and so this should default to that.
    // Otherwise it should default to general.
    if (isOnlyInstructor) return feedback;
    else return general;
  }

  addHypothesisSupporter = (category) => {
    const { hypothesisSupporters } = this.state;

    let newEntryID = crypto.randomUUID();

    const newEntry = {
      key: newEntryID,
      category,
      hypothesisKey: undefined,
      supports: undefined
    };

    let newHypothesisSet = clone(hypothesisSupporters);

    newHypothesisSet.push(newEntry);
    this.setState({
      hypothesisSupporters: newHypothesisSet,
      lastSeenCategorySet: newEntryID
    });
  };

  handleRemovingHypothesisSupporterData = (entryID: string) => {
    const { finding } = this.props;

    const findingID = (get(finding, "id") as string) || "placeholder";

    // Perform the actual removal
    // We can assume the component calling this is actually handling the confirm call
    // findingActions.removeHypothesisSupport(entryID, findingID);
  };

  removeHypothesisSupporter = (key: string) => {
    const { hypothesisSupporters } = this.state;

    // Find the linkage for this request
    // And find the finding also attached
    const hypothesisIndex = hypothesisSupporters.findIndex(
      (entry) => entry.key === key
    );

    this.handleRemovingHypothesisSupporterData(
      hypothesisSupporters[hypothesisIndex].hypothesisKey
    );

    let newHypothesisSupporters = remove(hypothesisSupporters, (n) => {
      return n.key !== key;
    });

    this.setState({
      hypothesisSupporters: newHypothesisSupporters
    });
  };
  handlePriorityChange = (options) => {
    if (options.length >= 1) {
      options = [options[options.length - 1]]; // Keep only the last selected option
    }
    this.setState({
      selectedOptions: options.length ? options : [],
      selectedRating: options.length ? options[0]?.value : 0
    });
  };

  saveHypothesisSupporter = (
    key: string,
    category: string,
    entry: string,
    supports: boolean
  ) => {
    let newHypothesisSupporters = clone(this.state.hypothesisSupporters);

    const hypothesisIndex = newHypothesisSupporters.findIndex(
      (entry) => entry.key === key
    );

    newHypothesisSupporters[hypothesisIndex] = {
      key,
      category,
      supports,
      hypothesisKey: entry
    };

    this.setState({
      hypothesisSupporters: newHypothesisSupporters
    });
  };

  getIsThereAnyLink = () => {
    const { hypothesisSupporters } = this.state;

    return hypothesisSupporters.length > 0;
  };

  renderLinks = () => {
    let { finding, canvasCategories, canvasEntries, theme } = this.props;
    const canEdit = this.canEditFinding();

    const { hypothesisSupporters } = this.state;

    let hypothesisLinks = get(
      finding,
      "hypothesisLinks"
    ) as HypothesisSupports[];

    //@ts-ignore
    const findingID = finding
      ? finding.id
        ? finding.id
        : finding.findingID
      : "placeholder";

    let otherTakenEntryIDs = [];

    for (let i = 0; i < hypothesisSupporters.length; i++) {
      //@ts-ignore
      otherTakenEntryIDs.push(
        hypothesisSupporters[i].hypothesisKey
          ? hypothesisSupporters[i].hypothesisKey
          : hypothesisSupporters[i].entryID
      );
    }

    let baseProps = {
      findingID: findingID,
      categories: canvasCategories,
      links: hypothesisLinks,
      workingLinks: hypothesisSupporters,
      otherTakenEntryIDs: otherTakenEntryIDs,
      entries: canvasEntries,
      save: this.saveHypothesisSupporter,
      remove: this.removeHypothesisSupporter,
      isSaving: this.props.isSaving,
      appState: this.props.appState,
      team: this.props.team,
      setValid: (isValid) => {
        this.setState({ isNewHypothesisValid: isValid });
      }
    };

    let children = [];

    let foundLinks = [];

    // show the saved links
    // for (let i = 0; i < this.props.base.findingLinks.length; i++) {
    //     const __link = this.props.base.findingLinks[i];

    //     if (__link.findingID === findingID) {

    //         let foundCategoryID = undefined;

    //         try {
    //             let matchingEntry = getEntryById(this.props.base, __link.entryID);
    //             //@ts-ignore
    //             foundCategoryID = matchingEntry.categoryID;
    //         }
    //         catch (e) {

    //         }

    //         children.push(
    //             <HypothesisFindingSupport
    //                 base={this.props.base}
    //                 {...baseProps}
    //                 baseAtomBridge={this.props.base}
    //                 shouldBeForSub={false}
    //                 valid={__link.entryID !== undefined}
    //                 linkID={__link.linkID} category={foundCategoryID} supports={__link.supports} entry={__link.entryID} />
    //         )
    //     }
    // }

    // now show the placeholder links below
    for (let i = 0; i < hypothesisSupporters.length; i++) {
      const hs = hypothesisSupporters[i];

      const autoOpen = hs.key === this.state.lastSeenCategorySet;

      children.push(
        <HypothesisFindingSupport
          canvas={this.props.canvas}
          isMenuOpen={autoOpen}
          base={this.props.base}
          baseAtomBridge={this.props.base}
          {...baseProps}
          shouldBeForSub={false}
          valid={hs.key !== undefined}
          linkID={hs.key}
          category={hs.category}
          supports={hs.supports}
          entry={hs.hypothesisKey}
          theme={theme}
        />
      );
    }

    const allFilledUp = hypothesisSupporters.every(
      (hs) =>
        hs.key !== undefined &&
        hs.category !== undefined &&
        hs.supports !== undefined &&
        hs.hypothesisKey !== undefined
    );

    return (
      <span style={{ width: "100%" }}>
        {children}
        <div style={{ width: "fit-content" }}>
          {allFilledUp && (
            <BmcSelector
              canvasData={this.props.canvas}
              disabled={!canEdit || this.props?.quote?.textCache === undefined}
              renderPlaceholder={true}
              handleCategoryClick={(cat) => {
                this.addHypothesisSupporter(cat);

                this.setState({
                  hasClickedOnPlaceholder: false
                });
              }}
              baseAtomBridge={this.props.base}
              appState={this.props.appState}
              team={this.props.team}
              currentCategory={undefined}
              categories={canvasCategories}
              handleClose={() =>
                this.setState({ hasClickedOnPlaceholder: false })
              }
              handleOpen={() =>
                this.setState({ hasClickedOnPlaceholder: true })
              }
            />
          )}
        </div>
      </span>
    );
  };

  getSaveButtonText = (anyChanges) => {
    // a finding is selected and we can update it
    const { isDraft } = this.props;

    if (isDraft && !anyChanges) {
      return "Accept";
    }

    if (!!this.props.finding) return "Update";

    // no finding is selected, show the user they're creating a new one
    return "Save";
  };

  renderLinkTitle = () => {
    const { hasClickedOnPlaceholder } = this.state;

    let isThereALink = this.getIsThereAnyLink() || hasClickedOnPlaceholder;

    let lineDesign = {
      height: "2px",
      margin: "auto",
      display: "inline-block",
      width: "90%",
      float: "right",
      borderBottom: "1px solid rgba(0,0,0,0.1)",
      marginTop: "7px"
    };

    const sectionTitle = "Canvas Links";

    let titleStyle = clone(SECTION_SUB_TITLE_STYLE);

    titleStyle["position"] = "absolute";

    if (!isThereALink) {
      titleStyle.display = "none";
      lineDesign.width = "100%";
    }

    return (
      <div
        style={{
          width: "100%",
          display: "block",
          height: "20px",
          marginTop: "5px",
          marginBottom: "5px"
        }}
      >
        <span style={titleStyle}>{sectionTitle}</span>
        <span style={lineDesign}></span>
        <div className="clearfix"></div>
      </div>
    );
  };

  renderCommentTitle = () => {
    const { comment } = this.state;

    let isThereAComment = !(comment === "");

    let lineDesign = {
      height: "2px",
      margin: "auto",
      display: "inline-block",
      width: "90%",
      float: "right",
      borderBottom: "1px solid rgba(0,0,0,0.1)",
      marginTop: "7px"
    };

    const sectionTitle = "Comment";

    let titleStyle = clone(SECTION_SUB_TITLE_STYLE);

    titleStyle["position"] = "absolute";

    if (!isThereAComment) {
      titleStyle.display = "none";
      lineDesign.width = "100%";
    }

    return (
      <div
        style={{
          width: "100%",
          display: "block",
          height: "20px",
          marginTop: "5px",
          marginBottom: "5px"
        }}
      >
        <span style={titleStyle}>{sectionTitle}</span>
        <span style={lineDesign}></span>
        <div className="clearfix"></div>
      </div>
    );
  };

  //
  // ALL NEW CODE FOR MULTI SELECT/ CUSTOM CATEGORIES

  formatCategoriesForDefault = (categories) => {
    if (categories === undefined) return [];

    let formattedData = [];
    for (let c of categories) {
      if (
        c.parentCustomFindingTypeID === "" &&
        c.options.visibility &&
        formattedData.length < 6
      ) {
        formattedData.push({
          customFindingTypeID: c.customFindingTypeID,
          label: c.label,
          value: c.label.toLowerCase(),
          tags: []
        });
      }
    }
    formattedData.push({ label: "Manage Tags", value: "managetags" });
    for (let g of formattedData) {
      for (let c of categories) {
        if (
          c.parentCustomFindingTypeID === g.customFindingTypeID &&
          c.options.visibility
        ) {
          g.tags.push({
            customFindingTypeID: c.customFindingTypeID,
            label: c.label,
            value: c.label.toLowerCase(),
            color: c.color
          });
        }
      }
    }

    return formattedData;
  };

  formatCategoriesForGrouped = (categories) => {
    if (categories === undefined) return [];

    let formattedData = [];
    for (let c of categories) {
      if (c.label === "Manage Tags") {
        continue;
      }
      if (c.parentCustomFindingTypeID === "" && c.options.visibility) {
        formattedData.push({
          customFindingTypeID: c.customFindingTypeID,
          label: c.label,
          options: []
        });
      }
    }
    for (let g of formattedData) {
      for (let c of categories) {
        if (
          c.parentCustomFindingTypeID === g.customFindingTypeID &&
          c.options.visibility
        ) {
          g.options.push({
            label: c.label,
            value: c.label.toLowerCase(),
            color: c.color,
            customFindingTypeID: c.customFindingTypeID
          });
        }
      }
    }

    return formattedData;
  };

  formatCategoriesForManager = (categories) => {
    if (categories === undefined) return [];

    let formattedData = [];
    for (let c of categories) {
      if (c.parentCustomFindingTypeID === "") {
        // formattedData.push({ ...c, tags: [], expanded: false })
        formattedData.push({ ...c, tags: [] });
      }
    }
    for (let g of formattedData) {
      for (let c of categories) {
        if (c.parentCustomFindingTypeID === g.customFindingTypeID) {
          g.tags.push(c);
        }
      }
    }

    return formattedData;
  };


  handleResize = () => {
    let newWidth = Math.max(
      document.querySelector(".category-select-container").offsetWidth,
      370
    );

    this.setState((prevState) => ({
      ...prevState,
      customCatContainerWidth: newWidth
    }));
  };

  getRatingValueSafe = () => {
    try {
      return this.props.finding?.rating?.global?.rating || 0;
    } catch (e) {}
    return 0;
  };

  // Warning: This causes selectedTags to be overwritten when creating a custom categorys
  componentDidUpdate(prevProps: any, prevState: any) {
    if (
      !isEqual(prevProps.quote, this.props.quote) ||
      !isEqual(prevProps.finding, this.props.finding)
    ) {
      const ratingValue = this.getRatingValueSafe();
      if (ratingValue > 0) {
        const matchingOptions = INSIGHT_PRIORITY_OPTIONS.filter((option) => {
          return (
            option.value === ratingValue ||
            (ratingValue === 1 && option.value === 2)
          );
        });

        this.setState({
          selectedOptions: matchingOptions
        });
      } else {
        this.setState({
          selectedOptions: []
        });
      }

      this.setState({
        comment: this.props.finding ? this.props.finding.comment : "",
        selectedTags: this.props.defaultTypes,
        selectedRating: this.getRatingValueSafe()
      });
    }
  }

  // componentDidUpdate(prevProps, prevState){
  //     if (this.state.inputValue !== prevState.inputValue){
  //         let filteredTagList = this.formatCategoriesForGrouped(this.props.customFindingTypes).map(g => (
  //             g.options.filter(t => t.label.toLowerCase().includes(this.state.inputValue.toLowerCase()))
  //         )).flat()

  //         console.log(filteredTagList)

  //         this.setState({
  //             tagSearchResults: filteredTagList,
  //             currentlyHighlightedEntry:
  //         })
  //     }

  // }

  async componentDidMount() {
    this.handleResize();
    window.addEventListener("resize", this.handleResize);

    let firstOption = this.formatCategoriesForDefault(
      this.props.customFindingTypes
    )[0];

    const ratingValue = this.getRatingValueSafe();
    if (ratingValue > 0) {
      const matchingOptions = INSIGHT_PRIORITY_OPTIONS.filter((option) => {
        return (
          option.value === ratingValue ||
          (ratingValue === 1 && option.value === 2)
        );
      });

      this.setState({
        selectedOptions: matchingOptions
      });
    } else {
      this.setState({
        selectedOptions: []
      });
    }

    this.setState((prevState) => ({
      ...prevState,
      hoveredItem: firstOption,
      // optionAnchor: document.getElementById(firstOption.customFindingTypeID),
      // categoryData: this.props.customFindingTypes,
      selectedTags: this.props.defaultTypes,
      selectedRating: this.getRatingValueSafe(),
      initialized: true,
      hypothesisSupporters: this.buildHypoSupporters(this.props)
    }));
  }

  statusHasChanged = (originalValue, newValue) => {
    if (originalValue !== newValue) {
      return true;
    }

    return false;
  };

  isValueValid = (value, type) => {
    if (type === "comment") {
      if (value !== null && value !== undefined) {
        return true;
      }
    }

    if (type === "tags") {
      return true;
    }

    if (type === "links") {
      let anyIncomplete = false;

      for (let i = 0; i < value.length; i++) {
        if (value[i].hypothesisKey === undefined) {
          anyIncomplete = true;
        }
      }

      return !anyIncomplete;
    }

    if (type === "rating") {
      return true;
    }

    return true;
  };

  getStatus = () => {
    const finding = this.props?.finding;
    const ratingFromProps = this.props?.finding?.rating?.global?.rating || 0;
    const { selectedRating: ratingFromState } = this.state;
    let originalComment = "";

    try {
      originalComment = finding.comment;
    } catch (e) {}

    let originalTags = "";
    let currentTags = "";

    try {
      //@ts-ignore
      if (finding) {
        //@ts-ignore
        if (finding.joinery) {
          //@ts-ignore
          finding.joinery.forEach((findingType) => {
            originalTags += findingType.customFindingTypeID;
          });
        }
      }

      //@ts-ignore
      if (this.state.selectedTags) {
        //@ts-ignore
        this.state.selectedTags.forEach((tag) => {
          //@ts-ignore
          currentTags += tag.customFindingTypeID;
        });
      }
    } catch (e) {
      appLog(e);
    }

    let originalLinks = "";
    let currentLinks = "";
    try {
      const originalSupports = this.buildHypoSupporters(this.props);
      const currentSupports = this.state.hypothesisSupporters;

      for (let i = 0; i < originalSupports.length; i++) {
        originalLinks +=
          originalSupports[i].hypothesisKey +
          originalSupports[i].key +
          originalSupports[i].supports.toString();
      }

      for (let i = 0; i < currentSupports.length; i++) {
        if (
          currentSupports[i]?.hypothesisKey === undefined ||
          currentSupports[i]?.key === undefined ||
          currentSupports[i]?.supports === undefined
        ) {
          currentLinks += "0";
        } else {
          currentLinks +=
            currentSupports[i].hypothesisKey +
            currentSupports[i].key +
            currentSupports[i].supports.toString();
        }
      }
    } catch (e) {
      console.log(e);
    }

    let hasQuote = false;

    if (finding) {
      hasQuote = true;
    } else {
      //@ts-ignore
      if (this.props?.quote?.textCache !== undefined) {
        hasQuote = true;
      }
    }

    return {
      changed: {
        comment: this.statusHasChanged(originalComment, this.state.comment),
        tags: this.statusHasChanged(originalTags, currentTags),
        links: this.statusHasChanged(originalLinks, currentLinks),
        rating: this.statusHasChanged(ratingFromProps, ratingFromState)
      },
      valid: {
        comment: this.isValueValid(this.state.comment, "comment"),
        tags: this.isValueValid(currentTags, "tags"),
        links: this.isValueValid(this.state.hypothesisSupporters, "links"),
        rating: this.isValueValid(ratingFromState, "rating")
      },
      hasQuote: hasQuote
    };
  };

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  filterTags = (inputValue) => {
    return this.formatCategoriesForGrouped(this.props.customFindingTypes).map(
      (g) => ({
        label: g.label,
        options: g.options.filter((t) =>
          t.label.toLowerCase().includes(inputValue.toLowerCase())
        )
      })
    );
  };

  promiseOptions = (inputValue) => {
    return new Promise((resolve) => {
      const filteredTags = this.filterTags(inputValue);

      resolve(filteredTags);
    });
  };

  handleInputChange = (e) => {
    e.persist();
    let firstOption = this.formatCategoriesForDefault(
      this.props.customFindingTypes
    )[0];
    this.setState((prevState) => ({
      ...prevState,
      inputValue: e.target.value,
      hoveredItem: firstOption,
      creatingNew: false
    }));
    // if (e.target.value === ''){
    //     setTimeout(() => this.setState({ optionAnchor: document.getElementById(firstOption.customFindingTypeID) }), 100000)

    // }
  };

  showPopoverTags = (categoryObj) => {
    let anchorEl = document.getElementById(categoryObj.customFindingTypeID);
    this.setState((prevState) => ({
      ...prevState,
      hoveredItem: categoryObj,
      optionAnchor: anchorEl
    }));
  };

  hidePopoverTags = () => {
    this.setState((prevState) => ({
      ...prevState,
      hoveredItem: this.formatCategoriesForDefault(
        this.props.customFindingTypes
      )[0],
      // hoveredItem: null,
      optionAnchor: null
    }));
  };

  formatGroupLabel = (data) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}
    >
      <span>{data.label}</span>

      <span
        style={{
          backgroundColor: "#EBECF0",
          borderRadius: "2em",
          color: "#172B4D",
          display: "inline-block",
          fontSize: 12,
          fontWeight: "normal",
          lineHeight: "1",
          minWidth: 1,
          padding: "0.16666666666667em 0.5em",
          textAlign: "center"
        }}
      >
        {data.options.length}
      </span>
    </div>
  );

  formatCreateLabel = (inputValue) => {
    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div
          style={{ overflow: "hidden", textOverflow: "ellipsis" }}
        >{`[Enter] to create "${inputValue}"`}</div>
        {/* <div style={{ color: 'grey', fontSize: '10px', alignSelf: 'flex-end' }}>{'[Shift] + [Enter] to group'}</div> */}
      </div>
    );
  };

  updateSelections = (newTag, e) => {
    // update selection here
    if (e && e.shiftKey) {
      this.setState((prevState) => ({
        ...prevState,
        inputValue: "",
        selectedTags: [newTag]
      }));
    } else {
      if (
        this.state.selectedTags.some(
          (t) => t.customFindingTypeID === newTag.customFindingTypeID
        )
      ) {
        let newSelectedTags = this.state.selectedTags.filter(
          (t) => t.customFindingTypeID !== newTag.customFindingTypeID
        );
        this.setState((prevState) => ({
          ...prevState,
          inputValue: "",
          selectedTags: newSelectedTags
        }));
      } else {
        this.setState((prevState) => ({
          ...prevState,
          inputValue: "",
          selectedTags: [...prevState.selectedTags, newTag]
        }));
      }
    }
  };

  createCustomTag = async (context, inputValue, parentCategory?) => {
    let inputAsOption = {
      value: inputValue.toLowerCase().replace(/\W/g, ""),
      label: inputValue,
      customFindingTypeID: crypto.randomUUID()
    };

    let existingCategory = this.props.customFindingTypes.find(
      (t) => t.label.toLowerCase() === inputValue.toLowerCase()
    );

    if (context === "SELECTOR") {
      if (existingCategory) {
        // User tried to create (duplicate) an existing category
        if (
          this.state.selectedTags.some(
            (t) => JSON.stringify(t) === JSON.stringify(inputAsOption)
          )
        ) {
          // Dupicate category is already selected on current insight
          this.clearInputValue();
        } else if (existingCategory.options.visibility === false) {
          // Existing category is not set to 'visible'
          let parentCategory = this.props.customFindingTypes.find(
            (t) =>
              t.customFindingTypeID ===
              existingCategory.parentCustomFindingTypeID
          );
          if (parentCategory.options.visibility === false) {
            this.props.baseUpdateCategory(parentCategory, {
              options: { ...parentCategory.options, visibility: true }
            });
          }
          this.props.baseUpdateCategory(existingCategory, {
            options: { ...existingCategory.options, visibility: true }
          });
          this.updateSelections(existingCategory, undefined);
        } else {
          // Duplicate category exists but isn't already selected
          this.updateSelections(existingCategory, undefined);
          this.props.baseUpdateCategory(existingCategory, {
            options: { ...existingCategory.options, visibility: true }
          });
        }
      } else {
        // Normal creation of a new category
        this.setState((prevState) => ({
          ...prevState,
          inputValue: ""
        }));

        let customTagsGroupID;
        if (parentCategory === undefined) {
          try {
            customTagsGroupID = this.props.customFindingTypes.find(
              (g) => g.label === "Custom Tags"
            ).customFindingTypeID;
          } catch (e) {
            // create group and add it to it instead
            customTagsGroupID = crypto.randomUUID();

            this.props.baseCreateCategory({
              customFindingTypeID: customTagsGroupID,
              label: "Custom Tags",
              color: null,
              options: { visibility: true, favorited: false, expanded: false },
              parentCustomFindingTypeID: ""
            });
          }
        } else if (parentCategory === "") {
          customTagsGroupID = parentCategory;
        } else {
          customTagsGroupID = parentCategory.customFindingTypeID;
        }
        let newCategory = {
          customFindingTypeID: inputAsOption.customFindingTypeID,
          label: inputValue,
          color: null,
          options: { visibility: true, favorited: false },
          parentCustomFindingTypeID: customTagsGroupID
        };

        this.props.baseCreateCategory(newCategory);
        // Make sure Custom Tags (really any group) is visible after every tag creation
        this.props.baseUpdateCategory(
          this.props.customFindingTypes.find(
            (g) => g.customFindingTypeID === customTagsGroupID
          ),
          { options: { ...customTagsGroupID.options, visibility: true } }
        );
        this.updateSelections(newCategory, undefined);
      }
      this.setState((prevState) => ({
        ...prevState,
        creatingNew: false
      }));
    }

    if (context === "MANAGER") {
      if (existingCategory) {
        alert(
          "An entry with that name already exists. Please make all tag and group names unique"
        );
        return false;
      }

      if (parentCategory === "") {
        let newGroup = {
          customFindingTypeID: crypto.randomUUID(),
          label: inputValue,
          color: null,
          options: { visibility: true, favorited: false, expanded: false },
          parentCustomFindingTypeID: ""
        };
        this.props.baseCreateCategory(newGroup);
        return { ...newGroup, tags: [] };
      } else {
        let newTag = {
          customFindingTypeID: crypto.randomUUID(),
          label: inputValue,
          color: null,
          options: {
            visibility: parentCategory.options.visibility,
            favorited: false
          },
          parentCustomFindingTypeID: parentCategory.customFindingTypeID
        };
        this.props.baseCreateCategory(newTag);
        return newTag;
      }
    }
  };

  // createAndGroup = (parentCategory) => {
  //     this.createCustomTag("SELECTOR", this.state.inputValue, parentCategory)
  //     this.setState(prevState => ({
  //         ...prevState,
  //         creatingNew: false,
  //     }))
  // }

  overrideEnterAndArrows = (e, props) => {
    e.persist();
    if (e.key === "Escape") {
      this.clearInputValue();
    }
    // if (e.key === 'Space') {
    //     TODO: Conflict between transcript editor 'Space' and input 'Space'
    // }

    if (
      [
        "Enter",
        "ArrowUp",
        "ArrowDown",
        "ArrowRight",
        "ArrowLeft",
        "Tab"
      ].includes(e.key)
    ) {
      e.stopPropagation();
      if (e.key === "Enter" && this.state.inputValue !== "" && e.shiftKey) {
        // this.setState(prevState => ({
        //     ...prevState,
        //     creatingNew: true,
        //   }))
      } else if (
        e.key === "Enter" &&
        this.state.inputValue !== "" &&
        !this.state.creatingNew
      ) {
        props.setValue([], "create-option", "");
        this.createCustomTag("SELECTOR", this.state.inputValue);
      } else if (
        e.key === "Enter" &&
        this.state.inputValue === "" &&
        this.state.creatingNew
      ) {
        this.clearInputValue();
      } else if (e.key === "Tab") {
        e.preventDefault();
      } else if (e.key === "ArrowDown") {
      }
    }
  };

  clearInputValue = () => {
    this.setState((prevState) => ({
      ...prevState,
      inputValue: "",
      creatingNew: false
    }));
  };

  openCategoryManager = () => {
    this.setState((prevState) => ({
      ...prevState,
      inputValue: "",
      creatingNew: false,
      managerModalOpen: true
    }));
  };

  closeCategoryManager = () => {
    this.setState((prevState) => ({
      ...prevState,
      managerModalOpen: false
    }));
  };

  // migrateGroupExpansion = () => {
  //     this.props.customFindingTypes.forEach(f => {
  //         if (f.parentCustomFindingTypeID === ''){
  //             this.props.baseUpdateCategory(f, { options: { ...f.options, expanded: false } })
  //         }
  //     })
  // }

  customSubOptionMenu = (props) => {
    const { theme } = this.props;
    if (this.state.hoveredItem) {
      if (this.state.optionAnchor === null) {
        return <span></span>;
      }

      let width = "170px";

      try {
        width = `${props.selectProps.containerWidth}px`;
      } catch (e) {}

      const optionAnchor = this.state.optionAnchor;

      return (
        // <Popper anchorEl={this.state.optionAnchor} open={!!this.state.optionAnchor} keepMounted placement={'right-start'} sx={{
        <Popper
          anchorEl={{
            getBoundingClientRect: () => {
              if (optionAnchor === undefined || optionAnchor === null) {
                // when we would flicker, flicker it somewhere the user can't see
                return {
                  x: 0,
                  y: 0,
                  width: 0,
                  height: 0,
                  top: -6000,
                  left: 0
                };
              } else {
                // console.log(optionAnchor);
              }
              return optionAnchor.getBoundingClientRect();
            }
          }}
          open={!!this.state.optionAnchor}
          keepMounted
          placement={"right-start"}
          sx={{
            backgroundColor: "#FFFFFF",
            height: "fit-content",
            overflow: "hidden",
            width: width,
            // left: `${props.selectProps.containerWidth + 4}px`,
            // width: '170px',
            margin: "-4px 4px !important",
            borderRadius: "4px",
            boxShadow:
              "rgb(0 0 0 / 10%) 0px 0px 0px 1px, rgb(0 0 0 / 10%) 0px 4px 11px",
            zIndex: theme.custom.menuZIndex
          }}
        >
          <ul
            style={{
              padding: "4px 0px",
              margin: "0px",
              overflowY: "auto",
              overflowX: "hidden",
              maxHeight: "248px"
            }}
          >
            {this.state.hoveredItem.tags.length > 0 ? (
              this.state.hoveredItem.tags.map((t) => {
                let shouldBeChecked = this.state.selectedTags.some(
                  (tag) => tag.label === t.label
                );
                return (
                  <li
                    key={t.label}
                    className="submenu-item"
                    onClick={(e) => {
                      this.updateSelections(t, e);
                    }}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 12px",
                      height: "40px"
                    }}
                  >
                    <div
                      style={{
                        alignSelf: "center",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                    >
                      {t.label}
                    </div>
                    <IwSwitch checked={shouldBeChecked} />
                  </li>
                );
              })
            ) : (
              <li
                key="placeholder"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 12px",
                  height: "40px"
                }}
              >
                <div
                  style={{
                    alignSelf: "center",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  No Tags in This Group Yet
                </div>
              </li>
            )}
          </ul>
        </Popper>
      );
    }
  };

  PriorityOptions = (props) => {
    return (
      <components.Option {...props}>
        <Stack direction="row" alignItems="center">
          <img
            src={props.data.icon.src}
            alt={props.data.label}
            style={{
              width: "16px",
              height: "16px",
              marginRight: "10px"
            }}
          />
          {props.children}
        </Stack>
      </components.Option>
    );
  };

  Option = (props) => {
    const { theme } = this.props;
    let adjustedProps = {
      ...props,
      innerProps: {
        ...props.innerProps,
        onMouseOver: null,
        onMouseMove: null
      }
    };

    let shouldBeChecked;
    if (this.state.inputValue !== "") {
      shouldBeChecked =
        this.state.selectedTags.filter((tag) => tag.label === props.data.label)
          .length !== 0;
    }

    let customSwitch = (
      <div
        className={shouldBeChecked ? "clicked" : "unclicked"}
        style={{
          backgroundColor: shouldBeChecked ? "#2372AD" : "grey",
          width: "26px",
          minWidth: "26px",
          height: "16px",
          borderRadius: "20px",
          padding: "2px",
          display: "flex",
          alignItems: "center"
        }}
      >
        <div
          style={{
            backgroundColor: "#FAFAFA",
            width: "12px",
            height: "12px",
            borderRadius: "100%"
          }}
        ></div>
      </div>
    );

    let customOption;
    if (props.selectProps.inputValue === "") {
      if (props.data.label === "Manage Tags") {
        customOption = (
          <div
            style={{
              width: `${props.selectProps.containerWidth}px`,
              overflow: "hidden",
              textOverflow: "ellipsis",
              cursor: "pointer"
            }}
            id={props.data.customFindingTypeID}
            onClick={this.openCategoryManager}
            onMouseEnter={() =>
              this.setState({ hoveredItem: null, optionAnchor: null })
            }
          >
            <components.Option {...props}>
              <div
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}
              >
                {props.data.label}
              </div>
            </components.Option>
          </div>
        );
      } else {
        customOption = (
          <div
            style={{
              width: `${props.selectProps.containerWidth}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: "40px",
              padding: "8px 12px",
              backgroundColor:
                this.state.hoveredItem &&
                this.state.hoveredItem.label === props.data.label
                  ? theme.custom.hoverOptionColor
                  : "transparent"
            }}
            id={props.data.customFindingTypeID}
            onMouseEnter={(e) => this.showPopoverTags(props.data)}
          >
            {/* <components.Option {...adjustedProps} > */}
            <div
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {props.data.label}
            </div>
            <KeyboardArrowRightIcon />
            {/* </components.Option> */}
          </div>
        );
      }
    } else {
      customOption = (
        <div
          onClick={(e) => {
            props.data.__isNew__ ? null : this.updateSelections(props.data, e);
          }}
          style={{ overflow: "hidden" }}
        >
          <components.Option {...props}>
            <div
              style={{
                alignSelf: "center",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                width: "100%",
                paddingLeft:
                  props.data.__isNew__ || props.selectProps.inputValue === ""
                    ? "0px"
                    : "10px"
              }}
            >
              {props.data.label}
            </div>
            {props.data.__isNew__ ? null : customSwitch}
          </components.Option>
        </div>
      );
    }

    return (
      <div id={props.data.customFindingTypeID} style={{ position: "relative" }}>
        {customOption}
        {this.state.hoveredItem &&
        this.state.hoveredItem.label === props.data.label
          ? this.customSubOptionMenu(props)
          : undefined}
      </div>
    );
  };

  Input = (props) => {
    return (
      <components.Input
        {...props}
        onInput={this.handleInputChange}
        onKeyDown={(e) => this.overrideEnterAndArrows(e, props)}
        value={props.selectProps.menuIsOpen ? this.state.inputValue : ""}
      />
    );
  };

  Control = (props) => {
    return (
      <div onClick={this.clearInputValue} onFocus={this.clearInputValue}>
        <components.Control
          className="top-level-discovery insight-creation-tags"
          {...props}
        />
      </div>
    );
  };

  MenuList = (props) => {
    return (
      <components.MenuList {...props}>{props.children}</components.MenuList>
    );
  };

  MultiValueRemove = (props) => {
    // </components.MultiValueRemove>
    // return <div className="remove-tag-icon" onClick={() => updateSelections(selectedTag)}>
    //     <CancelIcon sx={{ fontSize: '18px' }} />
    // </div>
    return <></>;
  };

  MultiValueLabel = (props) => {
    return (
      <>
        {createTagBadge(
          this.props.theme,
          props.data,
          this.updateSelections,
          true
        )}
      </>
    );
  };

  ClearIndicator = (props) => {
    return (
      <Tooltip title="Clear All">
        <IconButton onClick={() => this.setState({ selectedTags: [] })}>
          <Clear sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Tooltip>
    );
  };
  isAllHypothesisLinkChecked = () => {
    if (this.state.hypothesisSupporters.length !== 0) {
      for (let link of this.state.hypothesisSupporters) {
        if (link.hypothesisKey === undefined || link.supports === undefined) {
          return false;
        }
      }
    }
    return true;
  };

  renderSaveButton = () => {
    const canEdit = this.canEditFinding();
    const status = this.getStatus();
    const { hypothesisSupporters, comment, selectedTags, selectedRating } =
      this.state;
    const { isDraft, quote } = this.props;
    let anyInvalid =
      !status.valid.comment ||
      //  !status.valid.links ||
      !status.valid.tags ||
      !status.valid.rating;
    let anyChanges =
      status.changed.comment ||
      status.changed.links ||
      status.changed.tags ||
      status.changed.rating ||
      (hypothesisSupporters.length === 0 &&
        comment?.length === 0 &&
        selectedTags?.length === 0 &&
        !!quote?.textCache &&
        selectedRating === 0);

    let enabled =
      canEdit &&
      !anyInvalid &&
      (isDraft || anyChanges) &&
      (this.props.base?.roleSnapshot?.isTeamMember ||
        this.props.base?.roleSnapshot?.isInstructor);


    return anyInvalid ? (
      <Tooltip title={"This finding is missing required information"}>
        <IwButton
          color={anyChanges ? "primary" : "success"}
          sx={{ 
            marginLeft: "auto",
    ...(anyChanges
      ? {} // no custom style for primary
      : {
          backgroundColor: "#BFE3C0",
          color: "#000000", // optional text color
          "&:hover": {
            backgroundColor: "#A5D9B2",
            color: "#000000",
          },
        }),
           }}
          variant="contained"
          disabled={!enabled}
          onClick={() => {
            if (!this.isAllHypothesisLinkChecked()) {
              return renderErrorToast("Please complete all the steps to link a hypothesis to the insights")
            }
            this.save(this.state.hypothesisSupporters,anyChanges);
          }}
        >
          {this.getSaveButtonText(anyChanges)}
        </IwButton>
      </Tooltip>
    ) : (
      <IwButton
        color={anyChanges ? "primary" : "success"}
        sx={{ 
          marginLeft: "auto",
    ...(anyChanges
      ? {} // no custom style for primary
      : {
          backgroundColor: "#BFE3C0",
          color: "#000000", // optional text color
          "&:hover": {
            backgroundColor: "#A5D9B2",
            color: "#000000",
          },
        }),
         }}
        variant="contained"
        loading={this.props.createSaving}
        disabled={!enabled || this.props.createSaving}
        onClick={() => {
          if (!this.isAllHypothesisLinkChecked()) {
            return renderErrorToast("Please complete all the steps to link a hypothesis to the insights")
          }
          this.save(this.state.hypothesisSupporters,anyChanges);
        }}
      >
        {this.getSaveButtonText(anyChanges)}
      </IwButton>
    );
  };

  renderInsightInsertionType = () => {
     const {finding}=this.props;
     
      
     
     return (
       finding?.findingInsertionType && (
        <Tooltip title={`${finding?.findingInsertionType}`}>
          <IwButton
             variant="outlined"
             color="inherit" 
             disableRipple
             disableElevation
             sx={{
               border: "1px solid #c2c2c2",
               borderRadius: "4px",
               margin: "0px 10px 0px 0px",
               minWidth: "0 !important",
               padding: "5px 10px",
               cursor: "default",
               backgroundColor: "transparent",
               "&:hover": {
                 backgroundColor: "transparent",
                 border: "1px solid #c2c2c2",
               },
             }}
          >
            {aiIndicationIcons?.[finding?.findingInsertionType]}
          </IwButton>
        </Tooltip>
       )
     );
  }
  renderDeleteInsightButton = ()=>{
     const findingID = this.props.base?.selectedFinding;
     let quoteValue;
      try {
        quoteValue = JSON.parse(findingID?.quote);
      } catch (e) {
        quoteValue = null;
      }
     return (
       (findingID && quoteValue.source !==undefined) && (
        <Tooltip title={`Delete Insight`}>
              <IwButton
                onClick={(event) => {
                  event.stopPropagation();
                  this.props.onDelete(event);
                }}
                 sx={{
               border: "1px solid #c2c2c2",
               borderRadius: "4px",
               margin: "0px 10px 0px 0px",
               minWidth: "0 !important",
               padding: "6px 10px 7px 10px",
               cursor: "default",
               backgroundColor: "transparent",
               "&:hover": {
                 backgroundColor: "transparent",
                 cursor:"pointer",
                 border: "1px solid #c2c2c2",
               },
             }}
              >
            <DeleteOutlinedIcon sx={{
              color: "gray",
              fill:"none",
              width:"23px",
              height:"23px",
              ml:"3px",
              mt:'3px',
              "&:hover": {
                color: "#1976D2",
              },
            }}/>
              </IwButton>
        </Tooltip>
       )
     );
  }

  render() {
    let defaultOptions = this.formatCategoriesForDefault(
      this.props.customFindingTypes
    );
    const { defaultTypes } = this.props;
    const canEdit = this.canEditFinding();
    const { theme } = this.props;
    // console.log(this.state.hypothesisSupporters)
    const customStyles = {
      control: (provided, state) => ({
        ...provided,
        ...(state.menuIsOpen && { zIndex: 55 })
        // border:'2px solid red',
        // ":hover": {
        //   border: `1px solid ${scssVars.mainButtonHighlight}`,
        // },
      }),
      option: (provided, state) => ({
        ...provided,
        display: "flex",
        // justifyContent: 'flex-start',
        justifyContent: "space-between",
        alignItems: "center",
        height: "40px",
        cursor: state.data.label === "Manage Tags" ? "pointer" : "default"
      }),
      menuList: (provided, state) => ({
        ...provided,
        overflowY: state.selectProps.inputValue === "" ? "visible" : "auto"
        // overflowX: 'visible'
      }),
      menu: (provided, state) => ({
        ...provided,
        width:
          state.selectProps.inputValue === ""
            ? `${state.selectProps.containerWidth}px`
            : "100%",
        // width: `${state.selectProps.containerWidth}px`,
        // width: '100%',
        boxShadow:
          "rgb(0 0 0 / 10%) 0px 0px 0px 1px, rgb(0 0 0 / 10%) 0px 4px 11px",
        zIndex: theme.custom.menuZIndex,
        marginTop: "2px"
      }),
      valueContainer: (provided, state) => ({
        ...provided,
        padding: "2px 9px"
      }),
      dropdownIndicator: (provided, state) => ({
        ...provided
        // ':hover': {
        //     color: scssVars.mainButtonHighlight
        // }
      })
    };

    const customStylesPriorityOptions = {
      control: (styles) => ({
        ...styles,
        backgroundColor: "white",
        ":hover": {
          border: `1px solid ${theme.custom.mainButtonHighlight}`
        }
      }),
      menu: (provided, state) => ({
        ...provided,
        width:
          state.selectProps.inputValue === ""
            ? `${state.selectProps.containerWidth}px`
            : "100%",
        boxShadow:
          "rgb(0 0 0 / 10%) 0px 0px 0px 1px, rgb(0 0 0 / 10%) 0px 4px 11px",
        zIndex: theme.custom.menuZIndex,
        marginTop: "2px"
      }),

      multiValue: (styles, { data }) => ({
        ...styles,
        backgroundColor: "white",
        padding: "0px 6px"
      }),
      multiValueLabel: (styles, { data }) => ({
        ...styles,
        // width:'90px',
        color: "#000000",
        display: "flex",
        alignItems: "center",
        // justifyContent:'center',
        padding: "0px",
        fontSize: "14px",
        "&::before": {
          content: `url(${data.icon.src})`,
          width: "15px",
          height: "15px",
          marginRight: "8px"
        }
      }),
      multiValueRemove: (base) => ({ ...base, display: "none" })
    };

    const checkedIndicator = (
      <CheckCircleIcon
        sx={{
          color: theme.custom.mainButtonHighlight,
          backgroundColor: "white",
          padding: "1px",
          fontSize: "1.75rem"
        }}
      />
    );
    const warningIndicator = (
      <ErrorOutlinedIcon
        sx={{
          color: "#EB1c22",
          backgroundColor: "white",
          padding: "1px",
          fontSize: "1.75rem"
        }}
      />
    );
    const uncheckedStyles = {
      backgroundColor: "lightgrey",
      borderRadius: "100%",
      height: "23px",
      width: "23px",
      border: "4px solid white"
    };
    const uncheckedStylesRatings = {
      ...uncheckedStyles,
      marginTop: "6px"
    };
    const getLeftValue = (elementActive) => {
      if (elementActive) {
        return "-31px";
      } else {
        return "-30px";
      }
    };

    const isLinkChecked = () => {
      // if (this.state.hypothesisSupporters.length === 0) return false

      if (this.state.hypothesisSupporters.length === 0) return false;
      if (this.state.hypothesisSupporters.length !== 0) {
        for (let link of this.state.hypothesisSupporters) {
          if (
            link.hypothesisKey === undefined ||
            link.category === undefined ||
            link.supports === undefined
          ) {
            return false;
          }
        }
      }

      return true;
    };

    let findingEditor = (
      <div style={{ width: "100%" }}>
        <div
          style={{ position: "relative", display: "flex", marginLeft: "3px" }}
        >
          <div
            style={{
              position: "absolute",
              left: getLeftValue(Boolean(this?.props?.quote?.textCache)),
              top: "0px"
            }}
          >
            {this?.props?.quote?.textCache ? (
              checkedIndicator
            ) : (
              <div style={{ marginTop: "0px", ...uncheckedStyles }}></div>
            )}
          </div>
          {this.renderArtifact()}
        </div>

        {/* {this.renderCommentTitle()} */}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "20px",
            marginTop: "10px",
            position: "relative",
            marginLeft: "3px"
          }}
        >
          <div
            style={{
              position: "absolute",
              left: getLeftValue(isLinkChecked()),
              bottom: "5px"
            }}
          >
            {/* { this.state.hypothesisSupporters.length !== 0
           
                ? isLinkChecked()
                ? checkedIndicator
                : warningIndicator
                : <div style={uncheckedStyles}></div>
            } */}
            {isLinkChecked() ? (
              checkedIndicator
            ) : (
              <div style={uncheckedStyles}></div>
            )}
          </div>
          {this.renderLinks()}
        </div>

        <div
          style={{
            position: "relative",
            marginTop: "20px",
            display: "flex",
            alignItems: "center",
            marginLeft: "3px"
          }}
        >
          <div
            style={{
              position: "absolute",
              left: getLeftValue(this.state?.comment?.length !== 0)
            }}
          >
            {this.state?.comment?.length === 0 ? (
              <div style={uncheckedStyles}></div>
            ) : (
              checkedIndicator
            )}
          </div>
          <TextField
            id="outlined-multiline-flexible"
            placeholder="Enter a Comment"
            // label="Comment"
            multiline
            maxRows={4}
            value={this.state.comment}
            onChange={(e) => {
              this.setState({ comment: e.target.value });
            }}
            disabled={!canEdit || this?.props?.quote?.textCache === undefined}
            sx={{
              width: "100%",
              textFillColor: "#000 !important",
              fontSize:"15px",
              "& .MuiOutlinedInput-root": {
                fontSize:"15px",
                minHeight: "38px",
                padding: "8px 12px",
                "& fieldset": {
                  borderColor: theme.custom.borderGrey
                },
                "&:not(.Mui-disabled):hover fieldset": {
                  borderColor: this.props?.quote?.textCache
                    ? theme.custom.mainButtonHighlight
                    : theme.custom.borderGrey
                }
              },
              "& .Mui-disabled": {
                backgroundColor: "#f2f2f2",
                textFillColor: "#000 !important"
              }
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            marginTop: "10px",
            marginLeft: "3px"
          }}
        >

      <CategoryManagerModal
            base={this.props.base}
            baseDeleteCategory={this.props.baseDeleteCategory}
            baseToggleAllFavoritedTags={this.props.baseToggleAllFavoritedTags}
            baseUpdateCategory={this.props.baseUpdateCategory}
            createCustomTag={this.createCustomTag}
            customFindingTypes={this.props.customFindingTypes}
            isOpen={this.state.managerModalOpen}
            setIsOpen={this.closeCategoryManager}
          />

          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center"
            }}
          >
            <div
              style={{
                position: "absolute",
                left: getLeftValue(this.state.selectedTags.length !== 0)
              }}
            >
              {this.state.selectedTags.length === 0 ? (
                <div style={uncheckedStyles}></div>
              ) : (
                checkedIndicator
              )}
            </div>
            <div
              style={{
                width: `100%`,
                padding: "10px 0px 10px 0px",
                position: "relative"
              }}
            >
              <AsyncCreatableSelect
                isMulti
                isClearable
                openMenuOnFocus
                isDisabled={
                  !canEdit || this.props.quote?.textCache === undefined
                }
                defaultOptions={defaultOptions}
                loadOptions={this.promiseOptions.bind(this)}
                components={{
                  Option: this.Option,
                  Input: this.Input,
                  Control: this.Control,
                  MenuList: this.MenuList,
                  MultiValueRemove: this.MultiValueRemove,
                  MultiValueLabel: this.MultiValueLabel,
                  ClearIndicator: this.ClearIndicator
                }}
                closeMenuOnSelect={false}
                onMenuClose={this.hidePopoverTags}
                styles={customStyles}
                containerWidth={
                  this.state.inputValue === ""
                    ? Math.max(
                        (this.state.customCatContainerWidth - 160) / 2,
                        170
                      )
                    : this.state.customCatContainerWidth - 20
                }
                formatGroupLabel={this.formatGroupLabel}
                // onCreateOption={() => this.createCustomTag("SELECTOR", this.state.inputValue)}
                createOptionPosition="first"
                placeholder="Select Tag or Search..."
                formatCreateLabel={this.formatCreateLabel}
                value={this.state.selectedTags}
                menuShouldBlockScroll={true}
                // value={[]}
              />
            </div>
            <Tooltip title="Manage Tags">
              <IconButton
                sx={{ paddingRight: "0px" }}
                onClick={() => this.setState({ managerModalOpen: true })}
                disabled={!canEdit}
              >
                <LocalOffer />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        {/* {this.renderLinkTitle()} */}

        <div
          style={{
            display: "flex",
            alignItems: "end",
            justifyContent:"space-between",
            marginTop: "0px",
            marginLeft: "3px",
            position: "relative"
          }}
        >
          <div style={{ position: "absolute", left: "-30px", bottom: "8px" }}>
            {this.state.selectedRating === 0 ? (
              <div style={uncheckedStylesRatings}></div>
            ) : (
              <div style={{ position: "absolute", bottom: "-8px" }}>
                {checkedIndicator}
              </div>
            )}
          </div>
          <Tooltip title={INSIGHT_RATING_TOOLTIP_TEXT} placement="top">
            {/* <Rating
                        name="insight-rating"
                        value={this.state.selectedRating}
                        precision={1}
                        disabled={
                            !canEdit || this.props.quote.textCache === undefined
                        }
                        max={COMPONENT_GLOBAL_CONFIG.MAX_INSIGHT_RATING_STAR_COUNT}
                        onChange={(e, v) => {
                            this.setState({
                                selectedRating: v,
                            });
                        }}
                    /> */}

            <div
              style={{
                minWidth: "195px",
                maxWidth: `35%`,
                padding: "10px 0px 0px 0px",
                position: "relative"
                // border:'1px solid red'
              }}
            >
              <AsyncCreatableSelect
                isMulti
                isClearable
                openMenuOnFocus
                isDisabled={
                  !canEdit || this.props.quote?.textCache === undefined
                }
                defaultOptions={INSIGHT_PRIORITY_OPTIONS}
                components={{ Option: this.PriorityOptions }}
                styles={customStylesPriorityOptions}
                createOptionPosition="first"
                placeholder="Priority"
                onChange={this.handlePriorityChange}
                isSearchable={false}
                value={
                  this.state.selectedRating > 0
                    ? this.state.selectedOptions
                    : []
                }
              />
            </div>
          </Tooltip>
          <div>
            {this.renderInsightInsertionType()}
            {this.renderDeleteInsightButton()}
            {this.renderSaveButton()}
          </div>
        </div>
      </div>
    );

    const clickaway = (
      <Box
        className="dirty-insight-editor-clickblocker-effect"
        onClick={() => {
          this.setState({ confirmationDialogOpen: true });
        }}
      />
    );

    const shouldRenderClickaway = () => {
      const { finding, defaultTypes } = this.props;
      const { comment, hypothesisSupporters, selectedTags, selectedRating } =
        this.state;

      if (!!finding) {
        const status = this.getStatus();
        let anyChanges =
          status.changed.comment ||
          status.changed.links ||
          status.changed.tags ||
          status.changed.rating;

        if (anyChanges) return true;
      } else {
        if (!!selectedRating && selectedRating > 0) return true;
        if (comment.length > 0) return true;
        if (hypothesisSupporters.length > 0) return true;

        const newAddedTags = selectedTags.filter(
          (tag) => !defaultTypes.includes(tag)
        );
        const isNewTagAdded = newAddedTags.length > 0;
        const isDefaultTagRemoved =
          defaultTypes.length > 0 && selectedTags.length === 0;

        if (isNewTagAdded || isDefaultTagRemoved) return true;
      }

      return false;
    };

    return (
      <div
        id="joy-discovery-custom-categories"
        className="category-select-container"
      >
        <Dialog
          className="confirmation-dialog"
          open={this.state.confirmationDialogOpen}
          sx={{ zIndex: theme.custom.dialogZIndex }}
          onClose={() => {
            this.setState({ confirmationDialogOpen: false });
          }}
        >
          <DialogTitle>Discard unsaved Insight?</DialogTitle>
          {/* <DialogContent>
                    <DialogContentText>Navigating away from an Insight will delete all unsaved changes. Are you sure you want to continue?</DialogContentText>
                </DialogContent> */}
          <DialogActions sx={{ margin: "0px 16px 8px 0px" }}>
            <Button
              variant="contained"
              autoFocus
              onClick={() => this.setState({ confirmationDialogOpen: false })}
            >
              No
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                this.setState({
                  comment: "",
                  hypothesisSupporters: [],
                  selectedTags: defaultTypes,
                  selectedRating: 0,
                  selectedOptions: [],
                  confirmationDialogOpen: false
                });
                this.props.clearSelectedFinding();
              }}
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
        <div
          className={`top-level-discovery ${
            !!this.props?.quote?.selection && !!this.props?.quote?.textCache
              ? "insight-creation-active"
              : ""
          }
                 `}
          style={{
            backgroundColor: "white",
            padding: "20px 10px",
            // border:'2px solid blue',
            marginBottom: "12px",
            borderRadius: "4px",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            width: "100%",
            fontSize: "0.88rem",
            zIndex: 3 //this.state.comment.length > 0 || this.state.selectedTags.length > 0 ? scssVars.menuZIndex :
          }}
        >
          {this.renderFavorites()}
          <div style={{ display: "flex" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                margin: "20px 15px 15px 5px"
              }}
            >
              <Divider orientation="vertical" sx={{ height: "100%" }} />
            </div>
            {findingEditor}
          </div>
        </div>
        {shouldRenderClickaway() ? clickaway : null}
      </div>
    );
  }
}
